import React, { useState } from 'react';
import { Globe, LogIn, LogOut, UserPlus, User, Loader, Crown, Wrench, CreditCard } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import ServiceRegistration from './ServiceRegistration';
import { Capacitor } from '@capacitor/core';
import { STRIPE_PRICE_MONTHLY, STRIPE_PRICE_ANNUAL } from '../hooks/useStripe';

const Settings = ({ currency, onCurrencyChange, language, onLanguageChange, distanceUnit, onDistanceUnitChange, colorBlindMode, onColorBlindModeChange, theme, onThemeChange, isPro, offerings, offeringsError, serviceProducts, purchasing, restoring, rcError, onClearRcError, onPurchasePackage, onPurchaseServiceAccount, onRestorePurchases, onPresentPaywall, onRedeemPromoCode, onCancelSubscription, onFetchOfferings, showServiceRegistration, isServiceAccount, isWeb, onStripeCheckout, stripeError, onClearStripeError, stripePurchasing }) => {
    const { user, signIn, signUp, signOut } = useAuth();
    const [showServiceReg, setShowServiceReg] = useState(false);

    const [authMode, setAuthMode] = useState('login');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [authLoading, setAuthLoading] = useState(false);
    const [authError, setAuthError] = useState('');
    const [authSuccess, setAuthSuccess] = useState('');
    const [promoCode, setPromoCode] = useState('');
    const [promoStatus, setPromoStatus] = useState(null);
    const [promoMessage, setPromoMessage] = useState('');
    const [showSignOutConfirm, setShowSignOutConfirm] = useState(false);
    const [showLoginPrompt, setShowLoginPrompt] = useState(false);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [showCancelConfirm, setShowCancelConfirm] = useState(false);
    const [offeringsLoading, setOfferingsLoading] = useState(false);
    const [selectedPlanIdx, setSelectedPlanIdx] = useState(0);
    const [showWebMsg, setShowWebMsg] = useState(false);

    const handleUpgradeClick = () => {
        if (!user) {
            setShowLoginPrompt(true);
            return;
        }
        setSelectedPlanIdx(0);
        setShowWebMsg(false);
        setShowPaymentModal(true);
        // If packages not loaded yet, fetch them now
        if (!offerings?.availablePackages?.length && onFetchOfferings) {
            setOfferingsLoading(true);
            onFetchOfferings().finally(() => setOfferingsLoading(false));
        }
    };

    const currencies = [
        { code: 'USD', symbol: '$', name: 'US Dollar' },
        { code: 'EUR', symbol: '€', name: 'Euro' },
        { code: 'GBP', symbol: '£', name: 'British Pound' },

        { code: 'CNY', symbol: '¥', name: 'Chinese Yuan' },
        { code: 'INR', symbol: '₹', name: 'Indian Rupee' },
        { code: 'SAR', symbol: '﷼', name: 'Saudi Riyal' },
        { code: 'RUB', symbol: '₽', name: 'Russian Ruble' },
        { code: 'IDR', symbol: 'Rp', name: 'Indonesian Rupiah' },
        { code: 'PLN', symbol: 'zł', name: 'Polish Zloty' },
        { code: 'RSD', symbol: 'din', name: 'Serbian Dinar' },
        { code: 'TRY', symbol: '₺', name: 'Turkish Lira' },
        { code: 'NOK', symbol: 'kr', name: 'Norwegian Krone' },
        { code: 'SEK', symbol: 'kr', name: 'Swedish Krona' },
    ];

    const languages = [
        { code: 'en', name: 'English', flag: '🇬🇧' },
        { code: 'bg', name: 'Български', flag: '🇧🇬' },
        { code: 'zh', name: '中文', flag: '🇨🇳' },
        { code: 'hi', name: 'हिन्दी', flag: '🇮🇳' },
        { code: 'es', name: 'Español', flag: '🇪🇸' },
        { code: 'fr', name: 'Français', flag: '🇫🇷' },
        { code: 'ar', name: 'العربية', flag: '🇸🇦' },
        { code: 'pt', name: 'Português', flag: '🇵🇹' },
        { code: 'ru', name: 'Русский', flag: '🇷🇺' },
        { code: 'id', name: 'Bahasa Indonesia', flag: '🇮🇩' },
        { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
        { code: 'pl', name: 'Polski', flag: '🇵🇱' },
        { code: 'sr', name: 'Српски', flag: '🇷🇸' },
        { code: 'el', name: 'Ελληνικά', flag: '🇬🇷' },
        { code: 'tr', name: 'Türkçe', flag: '🇹🇷' },
        { code: 'no', name: 'Norsk', flag: '🇳🇴' },
        { code: 'sv', name: 'Svenska', flag: '🇸🇪' },
    ];

    const fallbackLabels = {
        settings: 'Settings', preferences: 'Preferences', currency: 'Currency',
        language: 'Language', distanceUnit: 'Distance Unit',
        account: 'Account', signIn: 'Sign In', register: 'Register',
        signOut: 'Sign Out', email: 'Email', password: 'Password',
        loggedInAs: 'Logged in as', noAccount: "Don't have an account?",
        hasAccount: 'Already have an account?', checkEmail: 'Check your email to confirm registration!',
        colorBlindMode: 'Color Blind Mode',
    };

    const currentTranslations = {
        en: { ...fallbackLabels },
        bg: {
            settings: 'Настройки', preferences: 'Предпочитания', currency: 'Валута',
            language: 'Език', distanceUnit: 'Единица за разстояние',
            account: 'Акаунт', signIn: 'Вход', register: 'Регистрация',
            signOut: 'Излизане', email: 'Имейл', password: 'Парола',
            loggedInAs: 'Влезли сте като', noAccount: 'Нямате акаунт?',
            hasAccount: 'Вече имате акаунт?', checkEmail: 'Проверете имейла си за потвърждение!',
            colorBlindMode: 'Режим за далтонисти',
        },
        zh: {
            settings: '设置', preferences: '偏好设置', currency: '货币',
            language: '语言', distanceUnit: '距离单位',
            account: '账户', signIn: '登录', register: '注册',
            signOut: '退出', email: '邮箱', password: '密码',
            loggedInAs: '已登录为', noAccount: '没有账户？',
            hasAccount: '已有账户？', checkEmail: '请查看邮箱确认注册！',
            colorBlindMode: '色盲模式',
        },
        hi: {
            settings: 'सेटिंग्स', preferences: 'प्राथमिकताएँ', currency: 'मुद्रा',
            language: 'भाषा', distanceUnit: 'दूरी इकाई',
            account: 'खाता', signIn: 'साइन इन', register: 'पंजीकरण',
            signOut: 'साइन आउट', email: 'ईमेल', password: 'पासवर्ड',
            loggedInAs: 'लॉग इन:', noAccount: 'खाता नहीं है?',
            hasAccount: 'पहले से खाता है?', checkEmail: 'पंजीकरण की पुष्टि के लिए ईमेल देखें!',
            colorBlindMode: 'वर्णान्धता मोड',
        },
        es: {
            settings: 'Configuración', preferences: 'Preferencias', currency: 'Moneda',
            language: 'Idioma', distanceUnit: 'Unidad de distancia',
            account: 'Cuenta', signIn: 'Iniciar sesión', register: 'Registrarse',
            signOut: 'Cerrar sesión', email: 'Correo', password: 'Contraseña',
            loggedInAs: 'Conectado como', noAccount: '¿No tienes cuenta?',
            hasAccount: '¿Ya tienes cuenta?', checkEmail: '¡Revisa tu correo para confirmar el registro!',
            colorBlindMode: 'Modo daltónico',
        },
        fr: {
            settings: 'Paramètres', preferences: 'Préférences', currency: 'Devise',
            language: 'Langue', distanceUnit: 'Unité de distance',
            account: 'Compte', signIn: 'Se connecter', register: 'S\'inscrire',
            signOut: 'Se déconnecter', email: 'E-mail', password: 'Mot de passe',
            loggedInAs: 'Connecté en tant que', noAccount: 'Pas de compte ?',
            hasAccount: 'Déjà un compte ?', checkEmail: 'Vérifiez votre e-mail pour confirmer l\'inscription !',
            colorBlindMode: 'Mode daltonien',
        },
        ar: {
            settings: 'الإعدادات', preferences: 'التفضيلات', currency: 'العملة',
            language: 'اللغة', distanceUnit: 'وحدة المسافة',
            account: 'الحساب', signIn: 'تسجيل الدخول', register: 'التسجيل',
            signOut: 'تسجيل الخروج', email: 'البريد الإلكتروني', password: 'كلمة المرور',
            loggedInAs: 'مسجل الدخول كـ', noAccount: 'ليس لديك حساب؟',
            hasAccount: 'لديك حساب بالفعل؟', checkEmail: 'تحقق من بريدك لتأكيد التسجيل!',
            colorBlindMode: 'وضع عمى الألوان',
        },
        pt: {
            settings: 'Definições', preferences: 'Preferências', currency: 'Moeda',
            language: 'Idioma', distanceUnit: 'Unidade de distância',
            account: 'Conta', signIn: 'Entrar', register: 'Registar',
            signOut: 'Terminar sessão', email: 'E-mail', password: 'Palavra-passe',
            loggedInAs: 'Ligado como', noAccount: 'Não tem conta?',
            hasAccount: 'Já tem conta?', checkEmail: 'Verifique o seu e-mail para confirmar o registo!',
            colorBlindMode: 'Modo daltónico',
        },
        ru: {
            settings: 'Настройки', preferences: 'Предпочтения', currency: 'Валюта',
            language: 'Язык', distanceUnit: 'Единица расстояния',
            account: 'Аккаунт', signIn: 'Войти', register: 'Зарегистрироваться',
            signOut: 'Выйти', email: 'Эл. почта', password: 'Пароль',
            loggedInAs: 'Вы вошли как', noAccount: 'Нет аккаунта?',
            hasAccount: 'Уже есть аккаунт?', checkEmail: 'Проверьте почту для подтверждения!',
            colorBlindMode: 'Режим дальтоника',
        },
        id: {
            settings: 'Pengaturan', preferences: 'Preferensi', currency: 'Mata Uang',
            language: 'Bahasa', distanceUnit: 'Satuan Jarak',
            account: 'Akun', signIn: 'Masuk', register: 'Daftar',
            signOut: 'Keluar', email: 'Email', password: 'Kata Sandi',
            loggedInAs: 'Masuk sebagai', noAccount: 'Belum punya akun?',
            hasAccount: 'Sudah punya akun?', checkEmail: 'Cek email Anda untuk konfirmasi pendaftaran!',
            colorBlindMode: 'Mode Buta Warna',
        },
        de: {
            settings: 'Einstellungen', preferences: 'Einstellungen', currency: 'Währung',
            language: 'Sprache', distanceUnit: 'Entfernungseinheit',
            account: 'Konto', signIn: 'Anmelden', register: 'Registrieren',
            signOut: 'Abmelden', email: 'E-Mail', password: 'Passwort',
            loggedInAs: 'Angemeldet als', noAccount: 'Kein Konto?',
            hasAccount: 'Bereits ein Konto?', checkEmail: 'Prüfen Sie Ihre E-Mail zur Bestätigung!',
            colorBlindMode: 'Farbenblind-Modus',
        },
        pl: {
            settings: 'Ustawienia', preferences: 'Preferencje', currency: 'Waluta',
            language: 'Język', distanceUnit: 'Jednostka odległości',
            account: 'Konto', signIn: 'Zaloguj się', register: 'Zarejestruj się',
            signOut: 'Wyloguj się', email: 'E-mail', password: 'Hasło',
            loggedInAs: 'Zalogowany jako', noAccount: 'Nie masz konta?',
            hasAccount: 'Masz już konto?', checkEmail: 'Sprawdź e-mail w celu potwierdzenia rejestracji!',
            colorBlindMode: 'Tryb dla daltonistów',
        },
        sr: {
            settings: 'Подешавања', preferences: 'Поставке', currency: 'Валута',
            language: 'Језик', distanceUnit: 'Јединица удаљености',
            account: 'Налог', signIn: 'Пријави се', register: 'Региструј се',
            signOut: 'Одјави се', email: 'Имејл', password: 'Лозинка',
            loggedInAs: 'Пријављен као', noAccount: 'Немате налог?',
            hasAccount: 'Већ имате налог?', checkEmail: 'Проверите имејл за потврду регистрације!',
            colorBlindMode: 'Режим за далтонисте',
        },
        el: {
            settings: 'Ρυθμίσεις', preferences: 'Προτιμήσεις', currency: 'Νόμισμα',
            language: 'Γλώσσα', distanceUnit: 'Μονάδα απόστασης',
            account: 'Λογαριασμός', signIn: 'Σύνδεση', register: 'Εγγραφή',
            signOut: 'Αποσύνδεση', email: 'Email', password: 'Κωδικός',
            loggedInAs: 'Συνδεδεμένος ως', noAccount: 'Δεν έχετε λογαριασμό;',
            hasAccount: 'Έχετε ήδη λογαριασμό;', checkEmail: 'Ελέγξτε το email σας για επιβεβαίωση!',
            colorBlindMode: 'Λειτουργία αχρωματοψίας',
        },
        tr: {
            settings: 'Ayarlar', preferences: 'Tercihler', currency: 'Para Birimi',
            language: 'Dil', distanceUnit: 'Mesafe Birimi',
            account: 'Hesap', signIn: 'Giriş Yap', register: 'Kayıt Ol',
            signOut: 'Çıkış Yap', email: 'E-posta', password: 'Şifre',
            loggedInAs: 'Oturum açıldı:', noAccount: 'Hesabınız yok mu?',
            hasAccount: 'Hesabınız var mı?', checkEmail: 'Kayıt onayı için e-postanızı kontrol edin!',
            colorBlindMode: 'Renk Körlüğü Modu',
        },
        no: {
            settings: 'Innstillinger', preferences: 'Preferanser', currency: 'Valuta',
            language: 'Språk', distanceUnit: 'Avstandsenhet',
            account: 'Konto', signIn: 'Logg inn', register: 'Registrer',
            signOut: 'Logg ut', email: 'E-post', password: 'Passord',
            loggedInAs: 'Innlogget som', noAccount: 'Har du ikke konto?',
            hasAccount: 'Har du allerede konto?', checkEmail: 'Sjekk e-posten din for å bekrefte registreringen!',
            colorBlindMode: 'Fargeblindmodus',
        },
        sv: {
            settings: 'Inställningar', preferences: 'Inställningar', currency: 'Valuta',
            language: 'Språk', distanceUnit: 'Avståndsenhet',
            account: 'Konto', signIn: 'Logga in', register: 'Registrera',
            signOut: 'Logga ut', email: 'E-post', password: 'Lösenord',
            loggedInAs: 'Inloggad som', noAccount: 'Har du inget konto?',
            hasAccount: 'Har du redan ett konto?', checkEmail: 'Kolla din e-post för att bekräfta registreringen!',
            colorBlindMode: 'Färgblindläge',
        },
    };

    const t = { ...fallbackLabels, ...(currentTranslations[language] || {}) };


    const inputStyle = {
        width: '100%',
        padding: '0.75rem',
        border: '1px solid var(--border-color)',
        borderRadius: 'var(--radius-md)',
        background: 'var(--bg-primary)',
        color: 'var(--text-primary)',
        fontSize: '1rem',
        boxSizing: 'border-box',
    };

    const selectStyle = { ...inputStyle, cursor: 'pointer' };

    const labelStyle = {
        display: 'block', marginBottom: '0.5rem',
        fontWeight: 500, fontSize: '0.9rem', color: 'var(--text-secondary)'
    };

    const handleAuth = async (e) => {
        e.preventDefault();
        setAuthLoading(true);
        setAuthError('');
        setAuthSuccess('');

        const { error } = authMode === 'login'
            ? await signIn(email, password)
            : await signUp(email, password);

        setAuthLoading(false);
        if (error) {
            setAuthError(error.message);
        } else if (authMode === 'register') {
            setAuthSuccess(t.checkEmail);
            setEmail(''); setPassword('');
        }
    };

    const handleSignOut = async () => {
        setShowSignOutConfirm(false);
        await signOut();
        setAuthError(''); setAuthSuccess('');
    };

    return (
        <div className="settings-container" style={{ maxWidth: '700px', margin: '0 auto' }}>
            <h2 className="text-2xl font-bold mb-6">{t.settings}</h2>

            {/* Account Card */}
            <div className="settings-card">
                <h3 style={{ margin: '0 0 1.25rem 0', fontSize: '1.1rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <User size={18} /> {t.account}
                </h3>

                {user ? (
                    /* Logged in state */
                    <div>
                        <div style={{
                            display: 'flex', alignItems: 'center', gap: '0.75rem',
                            padding: '0.75rem 1rem', background: 'var(--bg-secondary)',
                            borderRadius: 'var(--radius-md)', marginBottom: '1rem'
                        }}>
                            <div style={{
                                width: 36, height: 36, borderRadius: '50%',
                                background: '#738ADB',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                color: 'white', fontWeight: 700, fontSize: '1rem', flexShrink: 0
                            }}>
                                {user.email?.[0]?.toUpperCase()}
                            </div>
                            <div>
                                <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{t.loggedInAs}</div>
                                <div style={{ fontWeight: 600, fontSize: '0.95rem', wordBreak: 'break-all' }}>{user.email}</div>
                            </div>
                        </div>
                        <button
                            onClick={() => setShowSignOutConfirm(true)}
                            style={{
                                display: 'flex', alignItems: 'center', gap: '0.5rem',
                                padding: '0.65rem 1.25rem', border: '1px solid var(--border-color)',
                                borderRadius: 'var(--radius-md)', background: 'transparent',
                                color: '#ef4444', cursor: 'pointer', fontWeight: 500, fontSize: '0.95rem',
                                transition: 'background 0.2s'
                            }}
                        >
                            <LogOut size={16} /> {t.signOut}
                        </button>

                        {/* Sign out confirmation dialog */}
                        {showSignOutConfirm && (
                            <div onClick={() => setShowSignOutConfirm(false)} style={{
                                position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                zIndex: 9999, padding: '1rem'
                            }}>
                                <div onClick={e => e.stopPropagation()} style={{
                                    background: 'var(--modal-bg)', color: 'var(--header-text)',
                                    borderRadius: '20px', padding: '2rem', width: '100%',
                                    maxWidth: '340px', textAlign: 'center',
                                    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
                                    animation: 'fadeIn 0.15s ease-out'
                                }}>
                                    <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>👋</div>
                                    <h3 style={{ margin: '0 0 0.5rem', fontSize: '1.2rem', fontWeight: 700 }}>Sign out?</h3>
                                    <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', margin: '0 0 1.5rem', lineHeight: 1.5 }}>
                                        You'll need to sign back in to sync your CarTrack+ status across devices.
                                    </p>
                                    <div style={{ display: 'flex', gap: '0.75rem' }}>
                                        <button
                                            onClick={() => setShowSignOutConfirm(false)}
                                            style={{
                                                flex: 1, padding: '0.75rem',
                                                border: '1px solid var(--border-color)',
                                                borderRadius: 'var(--radius-md)', background: 'transparent',
                                                color: 'var(--text-primary)', fontWeight: 600,
                                                cursor: 'pointer', fontSize: '0.95rem'
                                            }}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            onClick={handleSignOut}
                                            style={{
                                                flex: 1, padding: '0.75rem',
                                                border: 'none', borderRadius: 'var(--radius-md)',
                                                background: '#ef4444', color: 'white',
                                                fontWeight: 600, cursor: 'pointer', fontSize: '0.95rem'
                                            }}
                                        >
                                            Sign Out
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                ) : (
                    /* Login / Register form */
                    <form onSubmit={handleAuth}>
                        {/* Tab switcher */}
                        <div style={{
                            display: 'flex', borderRadius: 'var(--radius-md)',
                            border: '1px solid var(--border-color)', overflow: 'hidden', marginBottom: '1.25rem'
                        }}>
                            {['login', 'register'].map(mode => (
                                <button
                                    key={mode} type="button"
                                    onClick={() => { setAuthMode(mode); setAuthError(''); setAuthSuccess(''); }}
                                    style={{
                                        flex: 1, padding: '0.6rem',
                                        background: authMode === mode ? '#738ADB' : 'transparent',
                                        color: authMode === mode ? 'white' : 'var(--text-secondary)',
                                        border: 'none', cursor: 'pointer', fontWeight: 600,
                                        fontSize: '0.9rem', transition: 'all 0.2s'
                                    }}
                                >
                                    {mode === 'login' ? t.signIn : t.register}
                                </button>
                            ))}
                        </div>

                        <div style={{ marginBottom: '1rem' }}>
                            <label style={labelStyle}>{t.email}</label>
                            <input
                                type="email" required value={email}
                                onChange={e => setEmail(e.target.value)}
                                placeholder="you@example.com"
                                style={inputStyle}
                            />
                        </div>

                        <div style={{ marginBottom: '1.25rem' }}>
                            <label style={labelStyle}>{t.password}</label>
                            <input
                                type="password" required value={password}
                                onChange={e => setPassword(e.target.value)}
                                placeholder="••••••••"
                                style={inputStyle}
                            />
                        </div>

                        {authError && (
                            <div style={{ color: '#ef4444', fontSize: '0.85rem', marginBottom: '1rem', padding: '0.6rem 0.75rem', background: '#fee2e2', borderRadius: 'var(--radius-md)' }}>
                                {authError}
                            </div>
                        )}
                        {authSuccess && (
                            <div style={{ color: '#16a34a', fontSize: '0.85rem', marginBottom: '1rem', padding: '0.6rem 0.75rem', background: '#dcfce7', borderRadius: 'var(--radius-md)' }}>
                                {authSuccess}
                            </div>
                        )}

                        <button
                            type="submit" disabled={authLoading}
                            style={{
                                width: '100%', padding: '0.75rem',
                                background: '#738ADB',
                                color: 'white', border: 'none', borderRadius: 'var(--radius-md)',
                                fontWeight: 600, fontSize: '1rem', cursor: authLoading ? 'not-allowed' : 'pointer',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
                                opacity: authLoading ? 0.7 : 1, transition: 'opacity 0.2s'
                            }}
                        >
                            {authLoading
                                ? <Loader size={16} style={{ animation: 'spin 1s linear infinite' }} />
                                : authMode === 'login' ? <><LogIn size={16} /> {t.signIn}</> : <><UserPlus size={16} /> {t.register}</>
                            }
                        </button>
                    </form>
                )}
            </div>

            {/* CarTrack+ Active Card — shown when user IS pro */}
            {isPro && (
                <div className="settings-card">
                    <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.1rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--text-primary)' }}>
                        <Crown size={20} color="#f59e0b" />
                        Subscription
                    </h3>

                    {/* Active badge */}
                    <div style={{
                        background: 'linear-gradient(135deg, rgba(245,158,11,0.12), rgba(115,138,219,0.12))',
                        border: '1px solid rgba(245,158,11,0.3)',
                        borderRadius: 'var(--radius-md)', padding: '0.9rem 1rem',
                        marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.75rem',
                    }}>
                        <span style={{ fontSize: '1.6rem' }}>👑</span>
                        <div>
                            <div style={{ fontWeight: 700, fontSize: '0.95rem' }}>CarTrack+ Active</div>
                            <div style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', marginTop: '0.1rem' }}>Ad-free · Unlimited cars · All features</div>
                        </div>
                    </div>

                    {/* Cancel button */}
                    <button
                        onClick={() => setShowCancelConfirm(true)}
                        style={{
                            width: '100%', background: 'transparent',
                            border: '1px solid var(--border-color)',
                            color: '#ef4444', padding: '0.7rem',
                            borderRadius: 'var(--radius-md)', fontSize: '0.9rem',
                            fontWeight: 600, cursor: 'pointer', textAlign: 'center',
                            transition: 'background 0.2s',
                        }}
                    >
                        Cancel Subscription
                    </button>
                </div>
            )}

            {/* Cancel Subscription Confirmation Modal */}
            {showCancelConfirm && (
                <div onClick={() => setShowCancelConfirm(false)} style={{
                    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    zIndex: 9999, padding: '1rem'
                }}>
                    <div onClick={e => e.stopPropagation()} style={{
                        background: 'var(--modal-bg)', color: 'var(--header-text)',
                        borderRadius: '24px', padding: '2rem', width: '100%',
                        maxWidth: '360px', textAlign: 'center',
                        boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
                        animation: 'fadeIn 0.15s ease-out'
                    }}>
                        <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>😢</div>
                        <h3 style={{ margin: '0 0 0.5rem', fontSize: '1.2rem', fontWeight: 700 }}>Cancel CarTrack+?</h3>
                        <p style={{ color: 'var(--text-secondary)', fontSize: '0.88rem', margin: '0 0 1.5rem', lineHeight: 1.6 }}>
                            You'll lose access to all premium features — unlimited cars, ad-free experience, and more.
                        </p>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
                            {/* For real RC subscriptions — open store management page */}
                            <button
                                onClick={() => {
                                    setShowCancelConfirm(false);
                                    const proSource = localStorage.getItem('proSource');
                                    if (proSource === 'sandbox' || proSource === 'promo') {
                                        // Local-only unlock — clear it directly
                                        localStorage.removeItem('isPro');
                                        localStorage.removeItem('proSource');
                                        onCancelSubscription();
                                    } else {
                                        // Real RC subscription — redirect to store
                                        const isIos = /iphone|ipad|ipod/i.test(navigator.userAgent);
                                        const url = isIos
                                            ? 'https://apps.apple.com/account/subscriptions'
                                            : 'https://play.google.com/store/account/subscriptions?package=com.statistic.cartrack';
                                        window.open(url, '_blank');
                                    }
                                }}
                                style={{
                                    width: '100%', padding: '0.8rem',
                                    background: '#ef4444', border: 'none',
                                    color: 'white', borderRadius: 'var(--radius-md)',
                                    fontWeight: 700, cursor: 'pointer', fontSize: '0.95rem',
                                }}
                            >
                                Yes, Cancel
                            </button>

                            <button
                                onClick={() => setShowCancelConfirm(false)}
                                style={{
                                    width: '100%', padding: '0.8rem',
                                    background: 'var(--ui-accent)', border: 'none',
                                    color: 'white', borderRadius: 'var(--radius-md)',
                                    fontWeight: 700, cursor: 'pointer', fontSize: '0.95rem',
                                }}
                            >
                                Keep CarTrack+ 👑
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Subscription Card — shown when NOT pro */}
            {!isPro && (
                <div className="settings-card">
                    <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.1rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--text-primary)' }}>
                        <Crown size={20} />
                        Subscription
                    </h3>

                    <div>
                        <div style={{ background: 'var(--bg-secondary)', padding: '1rem', borderRadius: 'var(--radius-md)', marginBottom: '1rem' }}>
                            <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>Free Plan</div>
                            <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>Ad-supported. Limited features.</div>
                        </div>

                        {/* Stripe Error (web) */}
                        {isWeb && stripeError && (
                            <div style={{ color: '#ef4444', fontSize: '0.85rem', marginBottom: '1rem', padding: '0.6rem 0.75rem', background: '#fee2e2', borderRadius: 'var(--radius-md)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span>{stripeError}</span>
                                <button onClick={onClearStripeError} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', fontWeight: 700 }}>✕</button>
                            </div>
                        )}

                        {/* RC Error (native) */}
                        {!isWeb && rcError && (
                            <div style={{ color: '#ef4444', fontSize: '0.85rem', marginBottom: '1rem', padding: '0.6rem 0.75rem', background: '#fee2e2', borderRadius: 'var(--radius-md)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span>{rcError}</span>
                                <button onClick={onClearRcError} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', fontWeight: 700 }}>✕</button>
                            </div>
                        )}

                        {/* ── WEB: Stripe plan selection ───────────────────── */}
                        {isWeb ? (
                            <div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
                                    {/* Monthly */}
                                    <button
                                        disabled={stripePurchasing}
                                        onClick={() => {
                                            if (!user) { setShowLoginPrompt(true); return; }
                                            onStripeCheckout(STRIPE_PRICE_MONTHLY);
                                        }}
                                        style={{
                                            padding: '1rem 0.5rem', border: '2px solid var(--border-color)',
                                            borderRadius: 'var(--radius-md)', background: 'var(--bg-secondary)',
                                            cursor: stripePurchasing ? 'not-allowed' : 'pointer',
                                            opacity: stripePurchasing ? 0.7 : 1,
                                            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.35rem',
                                            transition: 'all 0.2s',
                                        }}
                                        onMouseOver={e => { e.currentTarget.style.borderColor = '#738ADB'; e.currentTarget.style.background = 'rgba(115,138,219,0.08)'; }}
                                        onMouseOut={e => { e.currentTarget.style.borderColor = 'var(--border-color)'; e.currentTarget.style.background = 'var(--bg-secondary)'; }}
                                    >
                                        <span style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--text-primary)' }}>Monthly</span>
                                        <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>Billed monthly</span>
                                    </button>

                                    {/* Annual — highlighted */}
                                    <button
                                        disabled={stripePurchasing}
                                        onClick={() => {
                                            if (!user) { setShowLoginPrompt(true); return; }
                                            onStripeCheckout(STRIPE_PRICE_ANNUAL);
                                        }}
                                        style={{
                                            padding: '1rem 0.5rem', border: '2px solid #738ADB',
                                            borderRadius: 'var(--radius-md)', background: 'rgba(115,138,219,0.1)',
                                            cursor: stripePurchasing ? 'not-allowed' : 'pointer',
                                            opacity: stripePurchasing ? 0.7 : 1,
                                            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.35rem',
                                            position: 'relative', transition: 'all 0.2s',
                                        }}
                                    >
                                        <div style={{
                                            position: 'absolute', top: '-1px', left: '-1px', right: '-1px',
                                            background: '#738ADB', color: 'white',
                                            fontSize: '0.65rem', fontWeight: 800, padding: '0.2rem',
                                            textAlign: 'center', borderTopLeftRadius: '10px', borderTopRightRadius: '10px',
                                            letterSpacing: '0.05em', textTransform: 'uppercase',
                                        }}>Best Value</div>
                                        <span style={{ fontWeight: 700, fontSize: '0.9rem', color: '#738ADB', marginTop: '0.6rem' }}>Annual</span>
                                        <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>Save ~33%</span>
                                    </button>
                                </div>

                                <button
                                    disabled={stripePurchasing}
                                    onClick={() => {
                                        if (!user) { setShowLoginPrompt(true); return; }
                                        onStripeCheckout(STRIPE_PRICE_MONTHLY);
                                    }}
                                    style={{
                                        width: '100%', background: '#738ADB', border: 'none',
                                        color: 'white', padding: '0.85rem',
                                        borderRadius: 'var(--radius-md)', fontSize: '1rem',
                                        fontWeight: 700, cursor: stripePurchasing ? 'not-allowed' : 'pointer',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
                                        opacity: stripePurchasing ? 0.7 : 1, marginBottom: '0.5rem',
                                    }}
                                >
                                    <CreditCard size={18} />
                                    {stripePurchasing ? 'Redirecting to checkout…' : 'Upgrade to CarTrack+'}
                                </button>

                                <p style={{ textAlign: 'center', fontSize: '0.75rem', color: 'var(--text-secondary)', margin: '0.25rem 0 0' }}>
                                    Secure payment via Stripe · Cancel anytime
                                </p>
                            </div>
                        ) : (
                            /* ── NATIVE: RevenueCat paywall ─────────────────── */
                            <div>
                                <button
                                    disabled={purchasing || restoring}
                                    onClick={handleUpgradeClick}
                                    style={{
                                        width: '100%', background: '#738ADB',
                                        border: 'none', color: 'white', padding: '0.85rem',
                                        borderRadius: 'var(--radius-md)', fontSize: '1rem',
                                        fontWeight: 700, cursor: purchasing ? 'not-allowed' : 'pointer',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
                                        opacity: purchasing ? 0.7 : 1,
                                        marginBottom: '0.75rem',
                                    }}
                                >
                                    <Crown size={18} fill="white" />
                                    {purchasing ? 'Opening…' : 'Upgrade to CarTrack+'}
                                </button>

                                <button
                                    disabled={restoring || purchasing}
                                    onClick={onRestorePurchases}
                                    style={{
                                        width: '100%', background: 'transparent',
                                        border: 'none', color: 'var(--text-secondary)',
                                        padding: '0.5rem', fontSize: '0.85rem',
                                        cursor: restoring ? 'not-allowed' : 'pointer', textAlign: 'center',
                                    }}
                                >
                                    {restoring ? 'Restoring…' : 'Restore previous purchase'}
                                </button>
                            </div>
                        )}

                        {/* Promo code — available on both platforms */}
                        <div style={{ marginTop: '0.75rem', borderTop: '1px solid var(--border-color)', paddingTop: '0.75rem' }}>
                            <label style={{ ...labelStyle, marginBottom: '0.5rem' }}>Have a promo code?</label>
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                                <input
                                    type="text"
                                    value={promoCode}
                                    onChange={e => { setPromoCode(e.target.value); setPromoStatus(null); }}
                                    placeholder="Enter code…"
                                    style={{ ...inputStyle, flex: 1, textTransform: 'uppercase', letterSpacing: '0.05em' }}
                                />
                                <button
                                    onClick={async () => {
                                        if (!promoCode.trim() || promoStatus === 'loading') return;
                                        setPromoStatus('loading');
                                        setPromoMessage('');
                                        const result = await onRedeemPromoCode(promoCode);
                                        if (result.success) {
                                            setPromoStatus('success');
                                            setPromoCode('');
                                        } else {
                                            setPromoStatus('error');
                                            setPromoMessage(result.message || 'Invalid or expired promo code.');
                                        }
                                    }}
                                    disabled={promoStatus === 'loading'}
                                    style={{
                                        padding: '0.75rem 1rem', background: 'var(--ui-accent)',
                                        border: 'none', color: 'white', borderRadius: 'var(--radius-md)',
                                        fontWeight: 600, cursor: promoStatus === 'loading' ? 'not-allowed' : 'pointer',
                                        whiteSpace: 'nowrap', opacity: promoStatus === 'loading' ? 0.7 : 1,
                                    }}
                                >
                                    {promoStatus === 'loading' ? 'Checking…' : 'Redeem'}
                                </button>
                            </div>
                            {promoStatus === 'success' && (
                                <div style={{ color: '#16a34a', fontSize: '0.85rem', marginTop: '0.5rem' }}>✓ Code applied! Unlocking CarTrack+…</div>
                            )}
                            {promoStatus === 'error' && (
                                <div style={{ color: '#ef4444', fontSize: '0.85rem', marginTop: '0.5rem' }}>✕ {promoMessage}</div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Full-Screen Paywall */}
            {showPaymentModal && (
                <div style={{
                    position: 'fixed', inset: 0, zIndex: 9999,
                    background: 'linear-gradient(160deg, #c5cff5 0%, #a5b0e8 30%, #8a96df 60%, #7480d4 100%)',
                    display: 'flex', flexDirection: 'column', alignItems: 'center',
                    animation: 'fadeIn 0.3s ease-out', overflowY: 'auto',
                }}>
                    {/* Close */}
                    <button
                        onClick={() => setShowPaymentModal(false)}
                        style={{
                            position: 'fixed', top: 20, right: 20,
                            background: 'rgba(255,255,255,0.25)', border: 'none',
                            width: 38, height: 38, borderRadius: '50%',
                            color: '#1a1a2e', cursor: 'pointer', zIndex: 10000,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: '1rem', fontWeight: 700, backdropFilter: 'blur(4px)',
                        }}
                    >✕</button>

                    {/* Hero */}
                    <div style={{
                        display: 'flex', flexDirection: 'column', alignItems: 'center',
                        justifyContent: 'center', paddingTop: '80px', paddingBottom: '28px', gap: '14px',
                    }}>
                        <div style={{
                            width: 112, height: 112, borderRadius: '50%', background: '#5b6fd4',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            boxShadow: '0 12px 40px rgba(0,0,0,0.2)', border: '3px solid rgba(255,255,255,0.5)',
                        }}>
                            <svg viewBox="0 0 512 512" width="60" height="60">
                                <g transform="translate(100, 132) scale(1.2)" stroke="white" strokeWidth="36" strokeLinecap="round" strokeLinejoin="round" fill="none">
                                    <path d="M 30,120 L 30,80 Q 30,50 55,48 L 100,45 Q 115,45 130,58 L 165,85 Q 175,93 190,93 L 210,93 Q 225,93 230,108 L 230,135 Q 230,145 220,145 L 190,145 M 30,120 Q 30,145 40,145 L 70,145 M 95,145 L 165,145" />
                                    <circle cx="82" cy="145" r="16" />
                                    <circle cx="82" cy="145" r="4" fill="white" stroke="none" />
                                    <circle cx="178" cy="145" r="16" />
                                    <circle cx="178" cy="145" r="4" fill="white" stroke="none" />
                                </g>
                            </svg>
                        </div>
                        <h2 style={{ color: '#1a1a2e', fontSize: '2rem', fontWeight: 800, margin: 0, letterSpacing: '-0.02em' }}>
                            CarTrack
                        </h2>
                    </div>

                    {/* Plans + Actions */}
                    <div style={{ width: '100%', padding: '0 20px 48px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {(() => {
                            const pkgs = offerings?.availablePackages ?? [];
                            const fallbackPlans = [
                                { label: 'Monthly', sublabel: '$9.99/mth', pkg: null },
                                { label: 'Annual', sublabel: '$79.98/yr ($6.66/mth)', pkg: null },
                            ];
                            const plans = pkgs.length > 0
                                ? pkgs.map(p => ({
                                    label: p.product?.title ?? (p.packageType === 'ANNUAL' ? 'Annual' : 'Monthly'),
                                    sublabel: p.product?.priceString ?? '',
                                    pkg: p,
                                }))
                                : fallbackPlans;
                            return plans.map((plan, idx) => {
                                const isSelected = selectedPlanIdx === idx;
                                return (
                                    <button
                                        key={idx}
                                        onClick={() => setSelectedPlanIdx(idx)}
                                        style={{
                                            width: '100%', padding: '16px 20px',
                                            background: isSelected ? 'rgba(140,120,210,0.45)' : 'rgba(255,255,255,0.88)',
                                            border: isSelected ? '2px solid rgba(140,120,210,0.7)' : '2px solid transparent',
                                            borderRadius: 16, cursor: 'pointer',
                                            display: 'flex', alignItems: 'center', gap: 14,
                                            backdropFilter: 'blur(12px)', textAlign: 'left', transition: 'all 0.2s',
                                            boxShadow: isSelected ? '0 4px 20px rgba(90,80,180,0.2)' : '0 2px 8px rgba(0,0,0,0.08)',
                                        }}
                                    >
                                        <div style={{
                                            width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
                                            background: isSelected ? '#5b6fd4' : 'transparent',
                                            border: isSelected ? 'none' : '2px solid #b0b8d0',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            transition: 'all 0.2s',
                                        }}>
                                            {isSelected && (
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                                                    <polyline points="20 6 9 17 4 12" />
                                                </svg>
                                            )}
                                        </div>
                                        <div>
                                            <div style={{ fontWeight: 700, fontSize: '1.05rem', color: isSelected ? '#3730a3' : '#1a1a2e', transition: 'color 0.2s' }}>
                                                {plan.label}
                                            </div>
                                            <div style={{ fontSize: '0.88rem', color: isSelected ? '#4338ca' : '#555', marginTop: 2, transition: 'color 0.2s' }}>
                                                {plan.sublabel}
                                            </div>
                                        </div>
                                    </button>
                                );
                            });
                        })()}

                        {/* Offerings error — shown when store connection fails */}
                        {offeringsError && !offerings && (
                            <div style={{
                                background: 'rgba(255,235,235,0.92)', borderRadius: 12,
                                padding: '10px 14px', fontSize: '0.82rem', color: '#7f1d1d',
                                textAlign: 'center', lineHeight: 1.5, backdropFilter: 'blur(8px)',
                                border: '1px solid rgba(239,68,68,0.3)',
                            }}>
                                ⚠️ Could not connect to the store. Please check your internet connection and try again.
                            </div>
                        )}
                        {showWebMsg && (
                            <div style={{
                                background: 'rgba(255,255,255,0.85)', borderRadius: 12,
                                padding: '12px 16px', fontSize: '0.88rem',
                                textAlign: 'center', lineHeight: 1.5, backdropFilter: 'blur(8px)',
                            }}>
                                {Capacitor.getPlatform() !== 'web' ? (
                                    // On a native device but store is unreachable
                                    <span style={{ color: '#b91c1c' }}>
                                        ⚠️ <strong>Could not connect to Google Play.</strong><br />
                                        Make sure you are on a <strong>real device</strong> with Google Play Services, signed into a Google account, and have an internet connection.
                                    </span>
                                ) : (
                                    // Running in a browser
                                    <span style={{ color: '#3730a3' }}>
                                        Purchases are only available on the <strong>CarTrack mobile app</strong>.
                                        Download it from the App Store or Google Play to subscribe.
                                    </span>
                                )}
                            </div>
                        )}
                        {/* Loading spinner while offerings fetch */}
                        {offeringsLoading && (
                            <div style={{ textAlign: 'center', padding: '8px', color: 'rgba(30,30,80,0.6)', fontSize: '0.85rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                                <Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> Connecting to store…
                            </div>
                        )}
                        <button
                            disabled={purchasing || offeringsLoading}
                            onClick={async () => {
                                setShowWebMsg(false);
                                const pkgs = offerings?.availablePackages ?? [];
                                if (pkgs.length > 0) {
                                    // Packages loaded — purchase directly
                                    const selected = pkgs[selectedPlanIdx] ?? pkgs[0];
                                    setShowPaymentModal(false);
                                    await onPurchasePackage(selected);
                                } else if (Capacitor.getPlatform() === 'web') {
                                    // On web browser — show informational message
                                    setShowWebMsg(true);
                                } else {
                                    // Native device: try to re-fetch offerings first, then purchase
                                    setOfferingsLoading(true);
                                    if (onFetchOfferings) await onFetchOfferings();
                                    setOfferingsLoading(false);
                                    // After fetch, try again with whatever loaded
                                    const freshPkgs = offerings?.availablePackages ?? [];
                                    if (freshPkgs.length > 0) {
                                        const selected = freshPkgs[selectedPlanIdx] ?? freshPkgs[0];
                                        setShowPaymentModal(false);
                                        await onPurchasePackage(selected);
                                    } else {
                                        // Still nothing — try the RC native paywall as last resort
                                        const purchased = await onPresentPaywall();
                                        if (purchased) {
                                            setShowPaymentModal(false);
                                        } else {
                                            setShowWebMsg(true);
                                        }
                                    }
                                }
                            }}
                            style={{
                                width: '100%', padding: '18px', background: '#3d3d5c',
                                border: 'none', color: 'white', borderRadius: 50,
                                fontSize: '1.1rem', fontWeight: 700,
                                cursor: (purchasing || offeringsLoading) ? 'not-allowed' : 'pointer',
                                opacity: (purchasing || offeringsLoading) ? 0.7 : 1, marginTop: 8,
                                boxShadow: '0 8px 24px rgba(30,30,60,0.35)', transition: 'opacity 0.2s',
                            }}
                        >
                            {purchasing ? 'Processing…' : offeringsLoading ? 'Connecting…' : 'Continue'}
                        </button>

                        {/* Restore */}
                        <button
                            onClick={onRestorePurchases} disabled={restoring}
                            style={{
                                background: 'none', border: 'none',
                                color: 'rgba(30,30,80,0.5)', fontSize: '0.85rem',
                                cursor: restoring ? 'not-allowed' : 'pointer',
                                textAlign: 'center', padding: '4px',
                            }}
                        >
                            {restoring ? 'Restoring…' : 'Restore purchases'}
                        </button>

                    </div>
                </div>
            )}


            {/* Login Prompt Modal for Purchase */}


            {showLoginPrompt && (
                <div onClick={() => setShowLoginPrompt(false)} style={{
                    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    zIndex: 9999, padding: '1rem'
                }}>
                    <div onClick={e => e.stopPropagation()} style={{
                        background: 'var(--modal-bg)', color: 'var(--header-text)',
                        borderRadius: '20px', padding: '2rem', width: '100%',
                        maxWidth: '340px', textAlign: 'center',
                        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
                        animation: 'fadeIn 0.15s ease-out'
                    }}>
                        <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>🔒</div>
                        <h3 style={{ margin: '0 0 0.5rem', fontSize: '1.2rem', fontWeight: 700 }}>Account Required</h3>
                        <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', margin: '0 0 1.5rem', lineHeight: 1.5 }}>
                            Please sign in or register an account first so we can link your CarTrack+ subscription to you!
                        </p>
                        <div style={{ display: 'flex', gap: '0.75rem' }}>
                            <button
                                onClick={() => setShowLoginPrompt(false)}
                                style={{
                                    flex: 1, padding: '0.75rem',
                                    border: '1px solid var(--border-color)',
                                    borderRadius: 'var(--radius-md)', background: 'transparent',
                                    color: 'var(--text-primary)', fontWeight: 600,
                                    cursor: 'pointer', fontSize: '0.95rem'
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => {
                                    setShowLoginPrompt(false);
                                    window.scrollTo({ top: 0, behavior: 'smooth' });
                                }}
                                style={{
                                    flex: 1, padding: '0.75rem',
                                    border: 'none', borderRadius: 'var(--radius-md)',
                                    background: 'var(--ui-accent)', color: 'white',
                                    fontWeight: 600, cursor: 'pointer', fontSize: '0.95rem'
                                }}
                            >
                                Sign In
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Service Provider Card */}
            {showServiceRegistration && user && !isServiceAccount && (
              <div className="settings-card">
                <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.1rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Wrench size={18} /> Service Provider
                </h3>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', margin: '0 0 1rem', lineHeight: 1.5 }}>
                  Own an auto service? Register to manage customer car maintenance records.
                </p>
                <button
                  onClick={() => setShowServiceReg(true)}
                  style={{
                    width: '100%', padding: '0.75rem',
                    background: '#738ADB',
                    border: 'none', borderRadius: 'var(--radius-md)', color: 'white',
                    fontWeight: 600, fontSize: '0.95rem', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
                  }}
                >
                  <Wrench size={16} /> Become a Service Provider
                </button>
              </div>
            )}

            {/* Service Account Active — hidden */}

            {/* Service Registration Modal */}
            {showServiceReg && (
              <div style={{
                position: 'fixed', inset: 0, background: 'var(--bg-primary)',
                zIndex: 99999, overflowY: 'auto', animation: 'fadeIn 0.2s ease-out',
                display: 'flex', flexDirection: 'column'
              }}>
                <ServiceRegistration onClose={() => setShowServiceReg(false)} language={language} onPurchaseServiceAccount={onPurchaseServiceAccount} serviceProducts={serviceProducts} />
              </div>
            )}

            {/* Preferences Card */}
            <div className="settings-card">
                <h3 style={{ margin: '0 0 1.5rem 0', fontSize: '1.1rem', fontWeight: 600 }}>{t.preferences}</h3>

                {/* Theme Picker */}
                <div style={{ marginBottom: '1.5rem' }}>
                    <label style={labelStyle}>Theme</label>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem' }}>
                        {[
                            {
                                key: 'light', label: 'Light',
                                bg: '#738ADB',
                                card: '#ffffff', header: '#738ADB'
                            },
                            {
                                key: 'dark', label: 'Dark',
                                bg: '#738ADB',
                                card: '#334155', header: '#738ADB'
                            },
                        ].map(th => (
                            <button
                                key={th.key}
                                onClick={() => onThemeChange(th.key)}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: '0.6rem',
                                    padding: '0.65rem 0.75rem',
                                    background: theme === th.key ? '#738ADB' : 'var(--bg-secondary)',
                                    color: theme === th.key ? '#fff' : 'var(--text-primary)',
                                    border: theme === th.key ? '2px solid #738ADB' : '2px solid var(--border-color)',
                                    borderRadius: 'var(--radius-md)', cursor: 'pointer',
                                    fontWeight: theme === th.key ? 700 : 500, fontSize: '0.85rem',
                                    transition: 'all 0.2s',
                                    boxShadow: theme === th.key ? '0 4px 12px rgba(99, 102, 241, 0.2)' : 'none',
                                }}
                            >
                                <span style={{
                                    display: 'inline-flex', flexShrink: 0,
                                    width: 32, height: 24, borderRadius: 6,
                                    background: th.bg,
                                    border: theme === th.key ? '1px solid rgba(255,255,255,0.4)' : '1px solid rgba(128,128,128,0.3)',
                                    overflow: 'hidden', position: 'relative',
                                    boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.1)'
                                }}>
                                    {/* Mock Header */}
                                    <span style={{
                                        position: 'absolute', top: 0, left: 0, right: 0,
                                        height: 6, background: th.header,
                                        opacity: 0.9
                                    }} />
                                    {/* Mock Content Card */}
                                    <span style={{
                                        position: 'absolute', top: 9, left: 4, right: 4, bottom: 3,
                                        borderRadius: 3,
                                        background: th.card,
                                        boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                                    }} />
                                </span>
                                {th.label}
                            </button>
                        ))}
                    </div>
                </div>

                <div style={{ marginBottom: '1.5rem' }}>
                    <label style={labelStyle}>{t.currency}</label>
                    <select value={currency} onChange={(e) => onCurrencyChange(e.target.value)} style={selectStyle}>
                        {currencies.map(curr => (
                            <option key={curr.code} value={curr.code}>{curr.name} ({curr.code})</option>
                        ))}
                    </select>
                </div>


                <div style={{ marginBottom: '1.5rem' }}>
                    <label style={labelStyle}>
                        <Globe size={16} style={{ display: 'inline', marginRight: '0.5rem', verticalAlign: 'middle' }} />
                        {t.language}
                    </label>
                    <select value={language} onChange={(e) => onLanguageChange(e.target.value)} style={selectStyle}>
                        {languages.map(lang => (
                            <option key={lang.code} value={lang.code}>{lang.name}</option>
                        ))}
                    </select>
                </div>

                <div style={{ marginBottom: '1.5rem' }}>
                    <label style={labelStyle}>{t.distanceUnit}</label>
                    <select value={distanceUnit} onChange={(e) => onDistanceUnitChange(e.target.value)} style={selectStyle}>
                        <option value="km">Kilometers (km)</option>
                        <option value="mi">Miles (mi)</option>
                    </select>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.5rem 0' }}>
                    <label style={{ ...labelStyle, marginBottom: 0 }}>{t.colorBlindMode}</label>
                    <label className="switch" style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px' }}>
                        <input
                            type="checkbox"
                            checked={colorBlindMode}
                            onChange={(e) => onColorBlindModeChange(e.target.checked)}
                            style={{ opacity: 0, width: 0, height: 0 }}
                        />
                        <span style={{
                            position: 'absolute', cursor: 'pointer', top: 0, left: 0, right: 0, bottom: 0,
                            backgroundColor: colorBlindMode ? 'var(--ui-accent)' : 'var(--border-color)',
                            transition: '.4s', borderRadius: '24px'
                        }}>
                            <span style={{
                                position: 'absolute', content: '""', height: '18px', width: '18px',
                                left: colorBlindMode ? '22px' : '3px', bottom: '3px',
                                backgroundColor: 'white', transition: '.4s', borderRadius: '50%'
                            }}></span>
                        </span>
                    </label>
                </div>
            </div>

            <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
        </div>
    );
};

export default Settings;
